import type { SalesProductDto } from '@nova/shared';
import { Check, ChevronDown, Layers } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn.js';

/**
 * Multi-select for the SALES product catalog. Drives both the Marked list/map and
 * the neighbours view («hvilke produkter vi inkluderer»). An empty selection means
 * «all products». Grouped by category, keyboard-dismissable.
 */
export function ProductMultiSelect({
  products,
  selected,
  onChange,
  className,
}: {
  products: SalesProductDto[];
  selected: string[];
  onChange: (ids: string[]) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selectedSet = new Set(selected);
  const toggle = (id: string) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  const label =
    selected.length === 0
      ? 'Alle produkter'
      : selected.length === 1
        ? (products.find((p) => p.id === selected[0])?.name ?? '1 produkt')
        : `${selected.length} produkter`;

  // Preserve catalog order (already sorted by sortOrder from the API), grouped.
  const groups = new Map<string, SalesProductDto[]>();
  for (const p of products) {
    const key = p.category ?? 'Annet';
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(p);
  }

  return (
    <div className={cn('relative', className)} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'border-hairline bg-base/40 text-ink inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm',
          selected.length > 0 && 'border-accent/40 text-accent',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Layers size={14} aria-hidden />
        {label}
        <ChevronDown size={14} aria-hidden className={cn('transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          className="border-hairline bg-panel absolute z-30 mt-1 max-h-80 w-64 overflow-y-auto rounded-lg border p-1 shadow-xl"
          role="listbox"
        >
          <button
            type="button"
            className="text-ink-soft hover:bg-raised/60 hover:text-ink flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-xs"
            onClick={() => onChange([])}
          >
            Alle produkter
            {selected.length === 0 && <Check size={13} className="text-accent" aria-hidden />}
          </button>
          {[...groups.entries()].map(([category, items]) => (
            <div key={category} className="mt-1">
              <p className="text-ink-soft/60 px-2.5 pb-0.5 pt-1 text-[10px] uppercase tracking-wide">{category}</p>
              {items.map((p) => {
                const on = selectedSet.has(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="option"
                    aria-selected={on}
                    onClick={() => toggle(p.id)}
                    className={cn(
                      'hover:bg-raised/60 flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-sm',
                      on ? 'text-ink' : 'text-ink-soft',
                    )}
                  >
                    <span className="truncate">{p.name}</span>
                    <span
                      className={cn(
                        'ml-2 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        on ? 'border-accent bg-accent/20' : 'border-hairline',
                      )}
                    >
                      {on && <Check size={11} className="text-accent" aria-hidden />}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
